import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Modal, Form, Input, Select, Switch, Flex } from "antd";
import {
  PaperClipOutlined,
  PictureOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { FetchPlaceholderFields } from "../../../../assets/api/ScriptsService";
import { fetchUserFilters } from "../../../../store/filters";
import {
  getTemplateComponents,
  EditTemplate,
} from "../../../../assets/api/templateService";
import CustomNotification from "../../../../Reuseable/CustomNotification/CustomNotification";
import NormalButton from "../../../../Reuseable/NormalButton/NormalButton";
import "./EmailEditTemplateModal.scss";

const { TextArea } = Input;
const { Option } = Select;

const EmailEditTemplateModal = ({ isOpen, onClose, templateData, onSave }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const { filter_list } = useSelector((state) => state.filters) || {};

  const [subjectText, setSubjectText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [languageOptions, setLanguageOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(false);

  const [placeholderFields, setPlaceholderFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [selectedBodyPlaceholder, setSelectedBodyPlaceholder] = useState(null);

  const dynamicPortfoliosOptions = useMemo(() => {
    const portfoliosData =
      filter_list?.find((item) => item?.name === "portfolios")?.filters || [];
    return portfoliosData.map((d) => ({
      value: d.id || d,
      label: d.name || d.key || d,
    }));
  }, [filter_list]);

  // Fetch API options on mount
  useEffect(() => {
    if (token && isOpen) {
      setLoadingComponents(true);
      getTemplateComponents(token)
        .then((res) => {
          if (res?.data?.data) {
            const data = res.data.data;
            if (Array.isArray(data.languages)) {
              setLanguageOptions(
                data.languages.map((l) => ({ value: l.code, label: l.label })),
              );
            }
            if (Array.isArray(data.categories)) {
              setCategoryOptions(
                data.categories.map((c) => ({
                  value: c.value,
                  label: c.label,
                })),
              );
            }
          }
        })
        .catch((err) =>
          console.error("Error fetching template components:", err),
        )
        .finally(() => setLoadingComponents(false));
    }
  }, [token, isOpen]);

  // Fetch placeholders
  useEffect(() => {
    if (token && isOpen) {
      setLoadingFields(true);
      FetchPlaceholderFields(token)
        .then((res) => {
          let dataArray = [];
          if (
            res?.data?.data?.field_names &&
            Array.isArray(res.data.data.field_names)
          ) {
            dataArray = res.data.data.field_names;
          } else if (
            res?.data?.field_names &&
            Array.isArray(res.data.field_names)
          ) {
            dataArray = res.data.field_names;
          } else if (Array.isArray(res?.data)) {
            dataArray = res.data;
          } else if (Array.isArray(res?.data?.data)) {
            dataArray = res.data.data;
          }

          const formatLabel = (str) => {
            if (!str) return "";
            return str
              .split("_")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(" ");
          };

          const options = dataArray.map((item) => {
            const strVal =
              typeof item === "string"
                ? item
                : item?.name || item?.key || String(item);
            return { value: strVal, label: formatLabel(strVal) };
          });

          setPlaceholderFields(options);
        })
        .catch((err) => console.error("Error fetching placeholders:", err))
        .finally(() => setLoadingFields(false));
    }
  }, [token, isOpen]);

  // Ensure filters are loaded
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchUserFilters());
    }
  }, [dispatch, isOpen]);

  // Prepopulate form when templateData changes
  useEffect(() => {
    if (isOpen) {
      if (templateData) {
        const subjectComp = templateData.components?.find(
          (c) => c.type === "SUBJECT",
        );
        const bodyComp = templateData.components?.find(
          (c) => c.type === "BODY",
        );
        const initialSubject = templateData.subject || subjectComp?.text || "";

        form.setFieldsValue({
          name: templateData.name,
          language: templateData.language,
          portfolio: templateData.portfolio_id,
          category: templateData.category,
          templateType: "default",
          is_active: templateData.is_active ?? false,
          subject: initialSubject,
          body: bodyComp?.text || "",
        });

        setSubjectText(initialSubject);
        setBodyText(bodyComp?.text || "");
      } else {
        form.resetFields();
        setSubjectText("");
        setBodyText("");
      }
    }
  }, [isOpen, templateData, form]);

  const handleBodyPlaceholderSelect = useCallback((val) => {
    if (!val) return;
    const currentBody = form.getFieldValue("body") || "";
    const placeholderText = `{{xbucketdata.${val}}}`;
    if (currentBody.length + placeholderText.length > 2048) {
      CustomNotification.show(
        "Placeholder cannot be added as it exceeds the 2048 characters limit.",
        "error",
      );
      setSelectedBodyPlaceholder(null);
      return;
    }
    const newBody = currentBody + placeholderText;
    form.setFieldsValue({ body: newBody });
    setBodyText(newBody);
    setSelectedBodyPlaceholder(null);
  }, [form]);

  const onFinish = useCallback(async (values) => {
    try {
      setSubmitting(true);

      const payload = {};

      // 1. Check Name
      if (values.name !== templateData?.name) {
        payload.name = values.name;
      }

      // 2. Check Language
      if (values.language !== templateData?.language) {
        payload.language = values.language;
      }

      // 3. Check Portfolio
      if (
        values.portfolio &&
        Number(values.portfolio) !== Number(templateData?.portfolio_id)
      ) {
        payload.portfolio_id = Number(values.portfolio);
      }

      // 4. Check Category
      if (values.category !== templateData?.category) {
        payload.category = values.category;
      }

      // 5. Check Status (is_active)
      if (
        values.is_active !== undefined &&
        !!values.is_active !== !!templateData?.is_active
      ) {
        payload.is_active = !!values.is_active;
      }

      // 6. Check Subject Change
      const currentSubject = values.subject || "";
      const originalSubject = templateData?.subject || "";
      if (currentSubject !== originalSubject) {
        payload.subject = currentSubject;
      }

      // 7. Check Components (only BODY)
      const bodyComp = templateData?.components?.find((c) => c.type === "BODY");
      const payloadComponents = [];

      // Check Body Change
      const currentBody = values.body || "";
      const originalBody = bodyComp?.text || "";
      if (currentBody !== originalBody) {
        payloadComponents.push({
          type: "BODY",
          text: currentBody,
        });
      }

      // Add components to payload if any changed
      if (payloadComponents.length > 0) {
        payload.components = payloadComponents;
      }

      // If no changes at all, notify and close
      if (Object.keys(payload).length === 0) {
        CustomNotification.show("No changes detected", "info");
        onClose();
        return;
      }

      const res = await EditTemplate(templateData.id, token, payload);
      if (res.status === 200 || res.status === 201) {
        CustomNotification.show("Template updated successfully", "success");
        onSave();
        onClose();
      }
    } catch (err) {
      CustomNotification.show(err || "Failed to update template", "error");
    } finally {
      setSubmitting(false);
    }
  }, [token, templateData, onClose, onSave]);

  const handleSubjectChange = useCallback((e) => {
    setSubjectText(e.target.value);
  }, []);

  const handleBodyChange = useCallback((e) => {
    setBodyText(e.target.value);
  }, []);

  return (
    <Modal
      title="Edit Email Template"
      open={isOpen}
      onCancel={onClose}
      width={1100}
      footer={null}
      centered
      className="emailEditTemplateModalMain"
    >
      <Flex
        wrap="wrap"
        gap={24}
        className="email-edit-template-flex-layout"
        style={{ height: "100%" }}
      >
        {/* Left Section - Form */}
        <div className="email-left-section">
          <div className="emailEditTemplateFormWrapper">
            <Form form={form} layout="vertical" onFinish={onFinish}>
              <Flex wrap="wrap" gap={16} className="emailFormFieldsGrid">
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[
                    { required: true, message: "Please enter template name" },
                  ]}
                >
                  <Input placeholder="Enter Name" />
                </Form.Item>
                <Form.Item
                  label="Language"
                  name="language"
                  rules={[
                    { required: true, message: "Please select language" },
                  ]}
                >
                  <Select
                    placeholder="Select Language"
                    options={languageOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Portfolio"
                  name="portfolio"
                  rules={[
                    { required: true, message: "Please select portfolio" },
                  ]}
                >
                  <Select
                    placeholder="Select Portfolio"
                    options={dynamicPortfoliosOptions}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Category"
                  name="category"
                  rules={[
                    { required: true, message: "Please select category" },
                  ]}
                >
                  <Select
                    placeholder="Select Category"
                    options={categoryOptions}
                    loading={loadingComponents}
                    allowClear
                  />
                </Form.Item>
                <Form.Item
                  label="Template Type"
                  name="templateType"
                  initialValue="default"
                  rules={[
                    { required: true, message: "Please select template type" },
                  ]}
                >
                  <Select placeholder="Default">
                    <Option value="default">Default</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Status"
                  name="is_active"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Form.Item>
              </Flex>

              <div className="emailTextAreasSection">
                <Form.Item
                  label="Subject"
                  name="subject"
                  rules={[{ required: true, message: "Please enter subject" }]}
                >
                  <Input
                    placeholder="Enter email subject"
                    onChange={handleSubjectChange}
                  />
                </Form.Item>

                {/* Body Section */}
                <div
                  className="email-custom-body-section"
                  style={{ marginBottom: "16px", marginTop: "16px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                    }}
                  >
                    <label
                      className="ant-form-item-required"
                      style={{ color: "#212330", fontWeight: 500 }}
                    >
                      Body
                    </label>
                    <Select
                      showSearch
                      placeholder="Select Placeholder"
                      style={{ width: "150px" }}
                      options={placeholderFields}
                      loading={loadingFields}
                      value={selectedBodyPlaceholder}
                      onChange={handleBodyPlaceholderSelect}
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </div>
                  <Form.Item
                    name="body"
                    style={{ marginBottom: 0 }}
                    rules={[
                      { required: true, message: "Please enter body text" },
                    ]}
                  >
                    <TextArea
                      rows={5}
                      maxLength={2048}
                      onChange={handleBodyChange}
                      placeholder="Enter email body (HTML or plain text supported)"
                    />
                  </Form.Item>
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: "12px",
                      color: "#888",
                      marginTop: "4px",
                    }}
                  >
                    {bodyText.length}/2048
                  </div>
                </div>
              </div>

              <Flex
                wrap="wrap"
                gap={12}
                className="emailEditTemplateSubmitAction"
                style={{
                  marginTop: "24px",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <NormalButton
                  type="default"
                  onClick={onClose}
                  className="emailEditTemplateCancelBtn"
                  width="130px"
                >
                  Cancel
                </NormalButton>
                <NormalButton
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  className="emailEditTemplateSaveBtn"
                  width="130px"
                >
                  Save Changes
                </NormalButton>
              </Flex>
            </Form>
          </div>
        </div>

        {/* Right Section - Gmail Compose Mockup Preview */}
        <div
          className="email-right-section"
          style={{
            flex: "1 1 400px",
            minWidth: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <div className="gmailMockupWrapper" style={{ width: "100%" }}>
            <div
              className="gmail-mockup"
              style={{
                width: "100%",
                height: "520px",
                border: "1px solid #cfcfcf",
                borderRadius: "12px 12px 0 0",
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
              }}
            >
              <div
                className="gmail-header"
                style={{
                  backgroundColor: "#202124",
                  padding: "10px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: "#f1f3f4",
                  borderRadius: "10px 10px 0 0",
                }}
              >
                <span
                  className="gmail-title"
                  style={{
                    fontWeight: 500,
                    fontSize: "14px",
                    fontFamily: "Roboto, Arial, sans-serif",
                  }}
                >
                  Edit Message Preview
                </span>
                <div
                  className="gmail-header-actions"
                  style={{ display: "flex", gap: "14px", fontSize: "14px" }}
                >
                  <span className="action-btn">─</span>
                  <span className="action-btn">⤢</span>
                  <span className="action-btn">✕</span>
                </div>
              </div>

              <div
                className="gmail-recipients"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  borderBottom: "1px solid #f1f3f4",
                }}
              >
                <div
                  className="field-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    borderBottom: "1px solid #f1f3f4",
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif",
                  }}
                >
                  <span
                    className="label"
                    style={{ color: "#7f7f7f", width: "60px", flexShrink: 0 }}
                  >
                    To
                  </span>
                  <span
                    className="value text-muted"
                    style={{ color: "#b0b0b0" }}
                  >
                    recipients@example.com
                  </span>
                </div>
                <div
                  className="field-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "8px 16px",
                    borderBottom: "1px solid #f1f3f4",
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif",
                  }}
                >
                  <span
                    className="label"
                    style={{ color: "#7f7f7f", width: "60px", flexShrink: 0 }}
                  >
                    Subject
                  </span>
                  <span
                    className="value subject-val"
                    style={{ color: "#b0b0b0" }}
                  >
                    {subjectText || "Enter template subject"}
                  </span>
                </div>
              </div>

              <div
                className="gmail-body"
                style={{
                  flex: 1,
                  padding: "16px",
                  overflowY: "auto",
                  backgroundColor: "#fff",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  className="gmail-content"
                  style={{
                    fontFamily: "Roboto, Arial, sans-serif",
                    fontSize: "14px",
                    color: "#202124",
                    lineHeight: 1.5,
                    flexGrow: 1,
                  }}
                >
                  {bodyText ? (
                    <p
                      className="body-text"
                      style={{
                        whiteSpace: "pre-wrap",
                        margin: "0 0 16px 0",
                        wordBreak: "break-word",
                      }}
                    >
                      {bodyText}
                    </p>
                  ) : (
                    <span
                      className="body-text text-muted"
                      style={{ color: "#b0b0b0", fontStyle: "italic" }}
                    >
                      Type email body on the left to preview...
                    </span>
                  )}
                </div>
              </div>

              <div
                className="gmail-footer"
                style={{
                  borderTop: "1px solid #f1f3f4",
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  backgroundColor: "#fff",
                }}
              >
                <div
                  className="send-btn-group"
                  style={{
                    display: "flex",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <button
                    className="send-btn"
                    type="button"
                    disabled
                    style={{
                      backgroundColor: "#1a73e8",
                      color: "#fff",
                      border: "none",
                      padding: "8px 20px",
                      fontWeight: 500,
                      fontSize: "13px",
                      opacity: 0.6,
                      cursor: "not-allowed",
                    }}
                  >
                    Send
                  </button>
                  <button
                    className="send-dropdown"
                    type="button"
                    disabled
                    style={{
                      backgroundColor: "#1a73e8",
                      color: "#fff",
                      border: "none",
                      borderLeft: "1px solid #1664d4",
                      padding: "8px 8px",
                      opacity: 0.6,
                      cursor: "not-allowed",
                    }}
                  >
                    ▾
                  </button>
                </div>
                <div
                  className="gmail-toolbar"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    color: "#5f6368",
                    fontSize: "16px",
                    flexGrow: 1,
                  }}
                >
                  <span
                    className="tool-icon"
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    A
                  </span>
                  <span
                    className="tool-icon"
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    <PaperClipOutlined />
                  </span>
                  <span
                    className="tool-icon"
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    <PictureOutlined />
                  </span>
                  <span
                    className="tool-icon"
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    🔗
                  </span>
                  <span
                    className="tool-icon"
                    style={{ opacity: 0.5, cursor: "not-allowed" }}
                  >
                    ☺
                  </span>
                  <div style={{ marginLeft: "auto" }}>
                    <span
                      className="tool-icon"
                      style={{ opacity: 0.5, cursor: "not-allowed" }}
                    >
                      <DeleteOutlined />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Flex>
    </Modal>
  );
};

export default memo(EmailEditTemplateModal);
